// Paradise-Performance fillable deal packets — metadata, deal model, deal math,
// and the deal → AcroForm field mapping for the templates in
// /public/forms/deal-packets/*.pdf.
//
// The templates are the dealership's real DMS packets with every deal value
// removed and replaced by a fillable field (source + field audit:
// formc/pp-forms/templates/FIELD-MAP.md). Shared fields (buyer_name, vin,
// sale_date, ...) propagate to every page of a packet automatically; the
// per-cell money fields below were identified cell-by-cell against rendered
// pages, so each value lands exactly where DeskManager used to print it.
// Dealer identity (name, address, phone, GDN P169002) is preprinted static
// content inside the PDFs and never needs to be filled.

export const PAYMENT_FREQUENCIES = [
  { value: 'MONTHLY', periodsPerYear: 12 },
  { value: 'SEMI-MONTHLY', periodsPerYear: 24 },
  { value: 'BI-WEEKLY', periodsPerYear: 26 },
  { value: 'WEEKLY', periodsPerYear: 52 },
];

export const DEAL_TYPES = [
  { value: 'cash', label: 'Cash' },
  { value: 'financed', label: 'Financed (lender)' },
  { value: 'bhph', label: 'Buy Here Pay Here' },
  { value: 'wholesale', label: 'Wholesale' },
];

export const DEAL_PACKETS = [
  {
    id: 'bhph-packet',
    label: 'BHPH Deal Packet',
    file: '/forms/deal-packets/bhph-packet.pdf',
    pages: 34,
    dealTypes: ['bhph'],
    description: 'Retail installment contract, itemization, title app, disclosures — dealer-financed.',
  },
  {
    id: 'financed-packet',
    label: 'Financed Deal Packet',
    file: '/forms/deal-packets/financed-packet.pdf',
    pages: 36,
    dealTypes: ['financed'],
    description: 'Retail installment contract packet for lender-financed deals.',
  },
  {
    id: 'cash-purchase',
    label: 'Cash Purchase Packet',
    file: '/forms/deal-packets/cash-purchase.pdf',
    pages: 21,
    dealTypes: ['cash'],
    description: 'Purchase agreement, bill of sale, title app, disclosures for cash deals.',
  },
  {
    id: 'wholesale-packet',
    label: 'Wholesale Packet',
    file: '/forms/deal-packets/wholesale-packet.pdf',
    pages: 5,
    dealTypes: ['wholesale'],
    description: 'Dealer-to-dealer wholesale purchase packet.',
  },
  {
    id: 'texas-after-a-sale',
    label: 'Texas After-a-Sale Forms',
    file: '/forms/deal-packets/texas-after-a-sale.pdf',
    pages: 20,
    dealTypes: ['cash', 'financed', 'bhph'],
    supplemental: true,
    description: 'Post-sale support forms (title work, notices, collections).',
  },
  {
    id: 'post-sale-forms',
    label: 'Post-Sale Forms',
    file: '/forms/deal-packets/post-sale-forms.pdf',
    pages: 18,
    dealTypes: ['cash', 'financed', 'bhph'],
    supplemental: true,
    description: 'Additional post-sale forms (shared pages with the after-a-sale set).',
  },
];

export function packetsForDealType(dealType) {
  return DEAL_PACKETS.filter((p) => p.dealTypes.includes(dealType));
}

// ---------------------------------------------------------------------------
// Deal model
// ---------------------------------------------------------------------------

const today = () => new Date().toISOString().slice(0, 10);

export function emptyDealModel() {
  return {
    dealType: 'cash',
    saleDate: today(),
    salesperson: 'WAYNE WILDER',
    dealerRep: 'JACOB BROUSSARD',
    buyer: {
      firstName: '', lastName: '', email: '', phone: '',
      address: '', city: '', state: 'TX', zip: '',
    },
    vehicle: {
      vin: '', stockNumber: '', year: '', make: '', model: '',
      body: '', color: '', mileage: '', condition: 'USED',
    },
    trade: { hasTrade: false, year: '', make: '', model: '', allowance: 0, payoff: 0 },
    money: {
      salePrice: 0,
      accessoriesTotal: 0,
      rebate: 0,
      taxRate: 6.25,            // TX motor vehicle sales tax
      docFee: 299,
      titleFee: 33,             // TX title application fee (most counties)
      licenseRegFee: 50.75,     // buyers tag / registration base
      inventoryTaxRate: 0,      // dealer's VIT rate, % of sale price
      inventoryTaxAmount: 0,    // used as-is when the rate is 0
      windshieldFee: 0,
      countyRdBridgeFee: 10,    // Wise County road & bridge add-on
      processingFee: 4.75,      // TX processing & handling fee
      regFeeDps: 1,
      inspectionFee: 0,
      inspectionReplacementFee: 7.5, // TX inspection program replacement fee
      otherFees: 0,
      cashDown: 0,
      deferredDowns: [],        // [{ date: 'YYYY-MM-DD', amount: number }] max 4
      apr: 0,
      numPayments: 0,
      paymentFrequency: 'MONTHLY',
      firstPaymentDate: '',
      finalPaymentAmount: 0,    // balloon / odd last payment (bill of sale)
      finalPaymentDate: '',
      maxDeductible: '',        // BHPH physical-damage insurance max deductible
    },
    overrides: {},              // manual overrides of derived values, by key
  };
}

const num = (v) => {
  const x = typeof v === 'string' ? parseFloat(v.replace(/[$,]/g, '')) : parseFloat(v);
  return Number.isFinite(x) ? x : 0;
};
const round2 = (v) => Math.round((v + Number.EPSILON) * 100) / 100;

// ---------------------------------------------------------------------------
// Deal math — every derived value can be overridden in the review UI
// ---------------------------------------------------------------------------

export function computeDerived(model) {
  const m = model.money;
  const salePrice = num(m.salePrice);
  const accessories = num(m.accessoriesTotal);
  const rebate = num(m.rebate);
  const allowance = model.trade.hasTrade ? num(model.trade.allowance) : 0;
  const payoff = model.trade.hasTrade ? num(model.trade.payoff) : 0;

  const netTradeRaw = allowance - payoff;
  const netTrade = Math.max(netTradeRaw, 0);
  const negEquityPayoff = Math.max(payoff - allowance, 0); // LAW line 4A

  const deferredTotal = round2(
    (m.deferredDowns || []).reduce((s, d) => s + num(d.amount), 0)
  );
  const cashDown = num(m.cashDown);
  const totalDown = round2(netTrade + cashDown + deferredTotal);

  // TX motor vehicle tax: (price + accessories + doc fee − trade allowance − rebate) × rate
  const taxable = Math.max(round2(salePrice + accessories + num(m.docFee) - allowance - rebate), 0);
  const salesTax = round2(taxable * num(m.taxRate) / 100);

  const inventoryTax = num(m.inventoryTaxRate) > 0
    ? round2(salePrice * num(m.inventoryTaxRate) / 100)
    : round2(num(m.inventoryTaxAmount));

  // LAW retail installment contract itemization (BHPH / Financed packets)
  const cashPriceWithTax = round2(salePrice + accessories + salesTax); // line 1
  const unpaidBalance = round2(cashPriceWithTax - totalDown);          // line 3
  const otherCharges = round2(
    negEquityPayoff + inventoryTax + num(m.licenseRegFee) + num(m.titleFee) +
    num(m.inspectionReplacementFee) + num(m.docFee) + num(m.windshieldFee) +
    num(m.countyRdBridgeFee) + num(m.processingFee) + num(m.regFeeDps) + num(m.otherFees)
  );                                                                   // line 4
  const amountFinanced = round2(unpaidBalance + otherCharges);         // line 5

  // Payment schedule
  const freq = PAYMENT_FREQUENCIES.find((f) => f.value === m.paymentFrequency) || PAYMENT_FREQUENCIES[0];
  const nPay = Math.max(Math.round(num(m.numPayments)), 0);
  const r = num(m.apr) / 100 / freq.periodsPerYear;
  let paymentAmount = 0;
  if (nPay > 0 && amountFinanced > 0) {
    paymentAmount = r > 0
      ? (amountFinanced * r * Math.pow(1 + r, nPay)) / (Math.pow(1 + r, nPay) - 1)
      : amountFinanced / nPay;
    paymentAmount = round2(paymentAmount);
  }
  const finalPayment = num(m.finalPaymentAmount);
  const totalOfPayments = nPay > 0
    ? round2(finalPayment > 0 ? paymentAmount * (nPay - 1) + finalPayment : paymentAmount * nPay)
    : 0;
  const financeCharge = round2(Math.max(totalOfPayments - amountFinanced, 0));
  const totalSalePrice = round2(totalOfPayments + totalDown);

  // Purchase agreement (cash / wholesale packet, DeskManager layout)
  const paUnpaidBalance = round2(salePrice + accessories - allowance + payoff);
  const paSubtotal = round2(
    paUnpaidBalance + num(m.inspectionFee) + num(m.otherFees) + salesTax +
    num(m.licenseRegFee) + num(m.titleFee) + inventoryTax + num(m.docFee)
  );
  const paTotalDue = round2(paSubtotal - (model.dealType === 'wholesale' ? cashDown + deferredTotal : 0));

  // Form 130-U sales & use tax computation
  const titleSalesPrice = round2(salePrice + accessories + num(m.docFee) - rebate);
  const titleTaxable = Math.max(round2(titleSalesPrice - allowance), 0);
  const titleTax = round2(titleTaxable * num(m.taxRate) / 100);

  const equityAmount = round2(allowance - payoff);

  return {
    netTrade, negEquityPayoff, deferredTotal, totalDown,
    taxable, salesTax, inventoryTax,
    cashPriceWithTax, unpaidBalance, otherCharges, amountFinanced,
    paymentAmount, totalOfPayments, financeCharge, totalSalePrice,
    paUnpaidBalance, paSubtotal, paTotalDue,
    titleSalesPrice, titleTaxable, titleTax,
    equityAmount,
  };
}

// Labels for the derived values shown in the review/override UI
export const DERIVED_LABELS = {
  salesTax: 'Sales tax',
  inventoryTax: 'Dealer inventory tax',
  netTrade: 'Net trade-in',
  negEquityPayoff: 'Net trade-in payoff (neg. equity)',
  deferredTotal: 'Deferred down total',
  totalDown: 'Total down payment',
  cashPriceWithTax: 'Cash price incl. tax (contract line 1)',
  unpaidBalance: 'Unpaid balance of cash price',
  otherCharges: 'Total other charges & fees',
  amountFinanced: 'Amount financed',
  paymentAmount: 'Payment amount',
  totalOfPayments: 'Total of payments',
  financeCharge: 'Finance charge',
  totalSalePrice: 'Total sale price',
  paUnpaidBalance: 'Unpaid balance (purchase agreement)',
  paSubtotal: 'Subtotal (purchase agreement)',
  paTotalDue: 'Total due (purchase agreement)',
  titleSalesPrice: '130-U sales price',
  titleTaxable: '130-U taxable amount',
  titleTax: '130-U tax due',
  equityAmount: 'Trade equity amount',
};

export function effectiveDerived(model) {
  const derived = computeDerived(model);
  const out = { ...derived };
  for (const [key, value] of Object.entries(model.overrides || {})) {
    if (value !== '' && value !== null && value !== undefined && key in out) {
      out[key] = num(value);
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const fmtMoney = (v) => {
  const x = num(v);
  return x === 0 ? '' : x.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
// Always prints, even for 0.00 — for computed totals that belong on the form
const fmtMoney0 = (v) =>
  num(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtRate = (v) => (num(v) === 0 ? '' : String(num(v)));
const fmtInt = (v) => {
  const x = Math.round(num(v));
  return x === 0 ? '' : String(x);
};
const up = (s) => (s || '').toString().trim().toUpperCase();

export function fmtDate(value) {
  if (!value) return '';
  if (value instanceof Date) {
    return value.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  }
  const s = String(value);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/); // ISO date / timestamp
  if (m) return `${m[2]}/${m[3]}/${m[1]}`;
  return s;
}

const nowStamp = () => {
  const d = new Date();
  return `${fmtDate(d)} ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
};

// ---------------------------------------------------------------------------
// Field builders
// ---------------------------------------------------------------------------

// Fields whose names are shared across every page of a packet (see FIELD-MAP.md)
function sharedIdentityFields(model) {
  const b = model.buyer;
  const v = model.vehicle;
  const cityStZip = [up(b.city), [up(b.state), up(b.zip)].filter(Boolean).join(' ')]
    .filter(Boolean).join(', ');
  const fullName = up([b.firstName, b.lastName].filter(Boolean).join(' '));
  return {
    buyer_name: fullName,
    buyer_first_name: up(b.firstName),
    buyer_last_name: up(b.lastName),
    buyer_email: (b.email || '').trim(),
    buyer_address: up(b.address),
    buyer_city: up(b.city),
    buyer_state: up(b.state),
    buyer_zip: up(b.zip),
    buyer_city_state_zip: cityStZip,
    vin: up(v.vin),
    stock_number: up(v.stockNumber),
    vehicle_year: String(v.year || ''),
    vehicle_make: up(v.make),
    vehicle_model: up(v.model),
    vehicle_body: up(v.body),
    vehicle_color: up(v.color),
    vehicle_condition: up(v.condition || 'USED'),
    vehicle_full: [String(v.year || ''), up(v.make), up(v.model), up(v.vin)].filter(Boolean).join(' '),
    odometer_mileage: fmtInt(v.mileage),
    sale_date: fmtDate(model.saleDate),
    salesperson_name: up(model.salesperson),
    dealer_rep_name: up(model.dealerRep),
    payment_frequency: isFinanceType(model.dealType) ? up(model.money.paymentFrequency) : '',
    datetime_p1_1: nowStamp(), // AutoAp recall report timestamp slot
  };
}

const isFinanceType = (dealType) => dealType === 'bhph' || dealType === 'financed';

// LAW 553-TX retail installment contract, pages 2–3 (identical field names in
// the BHPH and Financed packets).
function lawContractFields(model, d) {
  const m = model.money;
  return {
    // p2 — federal Truth-in-Lending box + payment schedule
    yearly_rate_p2_1: fmtRate(m.apr) || '0',
    amount_p2_1: fmtMoney0(d.financeCharge),      // finance charge
    amount_p2_2: fmtMoney0(d.amountFinanced),     // amount financed
    amount_p2_3: fmtMoney0(d.totalOfPayments),    // total of payments
    payment_p2_1: fmtMoney0(d.totalDown),         // "your down payment of $"
    payment_p2_2: fmtMoney0(d.totalSalePrice),    // total sale price
    payments_p2_1: fmtInt(m.numPayments),         // number of payments
    payments_p2_2: fmtMoney0(d.paymentAmount),    // amount of each payment
    date_p2_1: fmtDate(m.firstPaymentDate),       // payments beginning
    // p3 — itemization of amount financed
    accessories_p3_1: fmtMoney0(num(m.salePrice) + num(m.accessoriesTotal)), // "CASH PRICE $" cell
    accessories_p3_2: fmtMoney0(d.salesTax),      // "SALES TAX $" cell
    amount_p3_1: fmtMoney0(d.cashPriceWithTax),   // (1) cash price total
    trade_p3_1: model.trade.hasTrade ? fmtMoney0(model.trade.allowance) : '',
    amount_p3_2: model.trade.hasTrade ? fmtMoney(model.trade.payoff) : '', // payoff made by seller
    net_trade_p3_1: model.trade.hasTrade ? fmtMoney0(d.netTrade) : '',
    cash_p3_1: fmtMoney0(num(m.cashDown) + d.deferredTotal),
    total_downpayment_p3_1: fmtMoney0(d.totalDown),   // (2)
    amount_p3_3: fmtMoney0(d.unpaidBalance),          // (3) unpaid balance
    net_trade_payoff_p3_1: fmtMoney(d.negEquityPayoff), // 4A
    price_p3_1: fmtMoney(d.inventoryTax),             // F dealer's inventory tax
    amount_p3_4: fmtMoney(m.licenseRegFee),           // I license/registration
    amount_p3_5: fmtMoney(m.titleFee),                // J title fee
    amount_p3_6: fmtMoney(m.inspectionReplacementFee),// K inspection program replacement
    amount_p3_7: fmtMoney(m.docFee),                  // N documentary fee
    amount_p3_8: fmtMoney(m.windshieldFee),           // O windshield sticker fee
    amount_p3_9: fmtMoney(m.countyRdBridgeFee),       // O county rd & bridge add-on
    amount_p3_10: fmtMoney(m.processingFee),          // O processing & handling
    amount_p3_11: fmtMoney(m.regFeeDps),              // O reg fee DPS
    amount_p3_12: fmtMoney0(d.otherCharges),          // (4) total other charges
    financed_p3_1: fmtMoney0(d.amountFinanced),       // (5) amount financed
  };
}

// Form 130-U "Application for Texas Title and/or Registration" tax box.
// Cell positions are identical across packets; only field names differ.
function titleAppFields(model, d, names) {
  const m = model.money;
  return {
    [names.rebate]: fmtMoney(m.rebate),               // "($ __ rebate has been deducted)"
    [names.salesPrice]: fmtMoney0(d.titleSalesPrice), // 38(a)
    [names.tradeIn]: model.trade.hasTrade ? fmtMoney0(model.trade.allowance) : '', // 38(b)
    [names.fmvDeduction]: '',                         // 38(c) dealers/lessors only
    [names.taxable]: fmtMoney0(d.titleTaxable),       // 38(d)
    [names.tax]: fmtMoney0(d.titleTax),               // 38(e)
    [names.penalty]: '',                              // 38(f)
    [names.taxPaidTo]: '',                            // 38(g)
    [names.totalDue]: fmtMoney0(d.titleTax),          // 38(h)
  };
}

// DeskManager purchase agreement (cash-purchase p7 / wholesale p2 — same layout).
function purchaseAgreementFields(model, d, p) {
  const m = model.money;
  const suffix = (base) => `${base}_p${p}_1`;
  const fields = {
    [suffix('sales_price')]: fmtMoney0(m.salePrice),
    [`amount_p${p}_1`]: fmtMoney(m.accessoriesTotal),      // optional accessories line
    [suffix('gross_trade_payoff')]: model.trade.hasTrade ? fmtMoney(model.trade.payoff) : '',
    [suffix('unpaid_balance')]: fmtMoney0(d.paUnpaidBalance),
    [`amount_p${p}_2`]: fmtMoney(m.inspectionFee),          // inspection fees
    [`amount_p${p}_3`]: fmtMoney(m.otherFees),              // other fees
    [suffix('sales_tax')]: fmtMoney0(d.salesTax),
    [suffix('license_registration')]: fmtMoney(m.licenseRegFee),
    [suffix('title')]: fmtMoney(m.titleFee),
    [suffix('dealer_inventory_tax')]: fmtMoney(d.inventoryTax),
    [suffix('documentary')]: fmtMoney(m.docFee),
    [suffix('subtotal')]: fmtMoney0(d.paSubtotal),
    [suffix('total_due')]: fmtMoney0(d.paTotalDue),
  };
  if (p === 2) {
    // wholesale packet only — cash-purchase has a preprinted N/A in this cell
    fields.cash_down_payment_p2_1 = fmtMoney(m.cashDown);
  }
  return fields;
}

// DeskManager bill of sale (cash-purchase p20).
function billOfSaleFields(model, d) {
  const m = model.money;
  const financed = isFinanceType(model.dealType);
  return {
    amount_p20_1: financed ? fmtRate(m.apr) : '',        // A.P.R.
    financed_p20_1: financed ? fmtMoney0(d.amountFinanced) : '',
    total_payments_p20_1: financed ? fmtMoney0(d.totalOfPayments) : '',
    amount_p20_3: financed ? fmtInt(m.numPayments) : '', // "# MONTHLY PAYMENTS OF"
    finance_charge_p20_1: financed ? fmtMoney0(d.paymentAmount) : '', // payment amount cell
    date_p20_1: financed ? fmtDate(m.firstPaymentDate) : '',
    amount_p20_4: financed ? fmtMoney(m.finalPaymentAmount) : '',     // "PLUS ONE LAST PAYMENT OF"
    date_p20_2: financed ? fmtDate(m.finalPaymentDate) : '',          // "DUE ON"
    amount_p20_2: fmtMoney0(m.salePrice),                // CASH PRICE
    amount_p20_5: fmtRate(m.taxRate),                    // SALES TAX RATE
    amount_p20_6: fmtMoney0(d.salesTax),                 // SALES TAX AMOUNT
    amount_p20_7: fmtRate(m.inventoryTaxRate),           // INVENTORY TAX RATE
    amount_p20_8: fmtMoney(d.inventoryTax),              // INVENTORY TAX AMOUNT
    amount_p20_9: fmtMoney(m.docFee),                    // DOCUMENTARY
    amount_p20_10: fmtMoney(m.titleFee),                 // TITLE APP & TERP TITLE
    amount_p20_11: fmtMoney(m.licenseRegFee),            // BUYERS TAG/ETAG
    amount_p20_12: fmtMoney(m.windshieldFee),            // WINDSHIELD STICKER
    amount_p20_13: fmtMoney(m.countyRdBridgeFee),        // COUNTY RD & BRIDGE ADD ON
    amount_p20_14: fmtMoney(m.processingFee),            // PROCESSING & HANDLING
    amount_p20_15: fmtMoney(m.otherFees),                // OTHER FEES
    amount_p20_16: fmtMoney(m.inspectionReplacementFee), // INSP. REPLACEMENT PROGRAM
    amount_p20_17: financed ? fmtMoney0(d.amountFinanced) : '', // AMOUNT FINANCE
  };
}

// "Texas Disclosure of Equity in Trade-in Motor Vehicle" — same layout in the
// cash (p15), BHPH (p29) and Financed (p32) packets.
function equityDisclosureFields(model, d, p) {
  if (!model.trade.hasTrade) return {};
  const suffix = (base) => `${base}_p${p}_1`;
  return {
    [suffix('dealership_allowance_trade')]: fmtMoney0(model.trade.allowance),
    [suffix('owed_trade')]: fmtMoney0(model.trade.payoff),
    [suffix('equity')]: fmtMoney0(d.equityAmount),
    [suffix('cash_price_vehicle')]: fmtMoney0(model.money.salePrice),
    [suffix('financed')]: isFinanceType(model.dealType) ? fmtMoney0(d.amountFinanced) : '',
  };
}

// Deferred down payment acknowledgement (BHPH p24 / Financed p30):
// first cell is the per-payment amount from the acknowledgement paragraph,
// then one row per scheduled deferred payment.
function deferredDownFields(model, p) {
  const downs = (model.money.deferredDowns || []).filter((x) => num(x.amount) > 0);
  if (downs.length === 0) return {};
  const out = { [`amount_p${p}_1`]: fmtMoney0(downs[0].amount) };
  downs.slice(0, 4).forEach((x, i) => {
    out[`amount_p${p}_${i + 2}`] = fmtMoney0(x.amount);
  });
  return out;
}

// ---------------------------------------------------------------------------
// Packet assembly
// ---------------------------------------------------------------------------

export function buildFieldValues(packetId, model) {
  const d = effectiveDerived(model);
  const m = model.money;
  const shared = sharedIdentityFields(model);

  switch (packetId) {
    case 'bhph-packet':
      return {
        ...shared,
        ...lawContractFields(model, d),
        ...titleAppFields(model, d, {
          rebate: 'sales_price_p14_1',
          salesPrice: 'amount_p14_1',
          tradeIn: 'sales_use_tax_computation_p14_1',
          fmvDeduction: 'amount_p14_4',
          taxable: '65_rebuilt_salvage_p14_1',
          tax: 'amount_p14_2',
          penalty: 'amount_p14_5',
          taxPaidTo: 'amount_p14_6',
          totalDue: 'amount_p14_3',
        }),
        ...deferredDownFields(model, 24),
        amount_p27_1: m.maxDeductible ? fmtMoney0(m.maxDeductible) : '', // insurance max deductible
        loan_p28_1: fmtMoney0(d.amountFinanced),   // notice to co-signer "Amount of Loan"
        ...equityDisclosureFields(model, d, 29),
        amount_p34_1: fmtMoney0(d.paymentAmount),  // repo order "Scheduled Payment Amount"
      };

    case 'financed-packet':
      return {
        ...shared,
        ...lawContractFields(model, d),
        ...titleAppFields(model, d, {
          rebate: 'amount_p8_2',
          salesPrice: 'amount_p8_1',
          tradeIn: 'amount_p8_3',
          fmvDeduction: 'amount_p8_7',
          taxable: 'amount_p8_4',
          tax: 'amount_p8_5',
          penalty: 'amount_p8_8',
          taxPaidTo: 'amount_p8_9',
          totalDue: 'amount_p8_6',
        }),
        ...deferredDownFields(model, 30),
        loan_p31_1: fmtMoney0(d.amountFinanced),
        ...equityDisclosureFields(model, d, 32),
      };

    case 'cash-purchase':
      return {
        ...shared,
        ...purchaseAgreementFields(model, d, 7),
        ...equityDisclosureFields(model, d, 15),
        ...billOfSaleFields(model, d),
        ...titleAppFields(model, d, {
          rebate: 'sales_price_p21_1',
          salesPrice: 'amount_p21_1',
          tradeIn: 'sales_use_tax_computation_p21_1',
          fmvDeduction: 'amount_p21_4',
          taxable: '65_rebuilt_salvage_p21_1',
          tax: 'amount_p21_2',
          penalty: 'amount_p21_5',
          taxPaidTo: 'amount_p21_6',
          totalDue: 'amount_p21_3',
        }),
      };

    case 'wholesale-packet':
      return {
        ...shared,
        ...purchaseAgreementFields(model, d, 2),
      };

    case 'texas-after-a-sale':
      return {
        ...shared,
        amount_p18_1: isFinanceType(model.dealType) ? fmtMoney0(d.paymentAmount) : '', // repo order scheduled payment
      };

    case 'post-sale-forms':
      return {
        ...shared,
        amount_p5_1: isFinanceType(model.dealType) ? fmtMoney0(d.paymentAmount) : '',
      };

    default:
      return shared;
  }
}

// ---------------------------------------------------------------------------
// Normalizers — build a deal model from the shapes that exist in the admin app
// ---------------------------------------------------------------------------

// TX body-style codes, the way a DMS prints them (the 130-U body cell is only
// a couple of characters wide).
const BODY_CODES = {
  truck: 'PK', pickup: 'PK', suv: 'LL', car: '4D', sedan: '4D', van: 'VN',
  coupe: 'CP', convertible: 'CV', wagon: 'SW', hatchback: 'HB',
  trailer: 'TL', motorcycle: 'MC',
};

function bodyFromVehicle(veh) {
  const t = (veh.body_style || veh.vehicle_type || '').toString().toLowerCase();
  return BODY_CODES[t] || up(veh.body_style || '');
}

// From the DealBuilder wizard state (src/components/admin/DealBuilder.jsx),
// plus the full inventory row when one was selected.
export function modelFromWizard(dealData, vehicleRow) {
  const model = emptyDealModel();
  const c = dealData.customer || {};
  const v = dealData.vehicle || {};
  const veh = vehicleRow || {};
  const pricing = dealData.pricing || {};
  const financing = dealData.financing || {};
  const trade = dealData.trade || {};

  model.dealType =
    financing.method === 'cash' ? 'cash'
    : financing.method === 'bhph' ? 'bhph'
    : financing.method === 'wholesale' ? 'wholesale'
    : 'financed';
  model.buyer = {
    firstName: c.firstName || '', lastName: c.lastName || '',
    email: c.email || '', phone: c.phone || '',
    address: c.address || '', city: c.city || '',
    state: c.state || 'TX', zip: c.zipCode || c.zip || '',
  };
  model.vehicle = {
    vin: v.vin || veh.vin || '',
    stockNumber: veh.stock_number || v.stockNumber || '',
    year: v.year || veh.year || '',
    make: v.make || veh.make || '',
    model: v.model || veh.model || '',
    body: bodyFromVehicle(veh),
    color: veh.exterior_color || '',
    mileage: veh.mileage || '',
    condition: (veh.condition || 'used').toUpperCase() === 'NEW' ? 'NEW' : 'USED',
  };
  model.trade = {
    hasTrade: !!trade.hasTradeIn,
    year: trade.year || '', make: trade.make || '', model: trade.model || '',
    allowance: num(trade.acv), payoff: num(trade.payoff),
  };
  const productsTotal = Object.values(dealData.products || {})
    .reduce((s, p) => (p && p.selected ? s + num(p.price) : s), 0);
  model.money.salePrice = num(pricing.vehiclePrice) - num(pricing.discount) - num(pricing.rebates);
  model.money.accessoriesTotal = productsTotal;
  model.money.docFee = num(pricing.docFee);
  model.money.licenseRegFee = num(pricing.registrationFee) || model.money.licenseRegFee;
  model.money.taxRate = num(pricing.salesTaxRate) || 6.25;
  model.money.cashDown = num(financing.downPayment);
  model.money.apr = num(financing.apr);
  model.money.numPayments = num(financing.termMonths);
  return model;
}

// From a saved `deals` row (optionally joined with vehicles_db2025).
export function modelFromDealRow(deal) {
  // A deal saved after this feature shipped carries the full model.
  if (deal && deal.forms_data && typeof deal.forms_data === 'object' && deal.forms_data.buyer) {
    const saved = deal.forms_data;
    return { ...emptyDealModel(), ...saved, money: { ...emptyDealModel().money, ...saved.money } };
  }
  const model = emptyDealModel();
  const veh = deal?.vehicles_db2025 || {};
  const name = (deal?.customer_name || '').trim();
  const spaceAt = name.lastIndexOf(' ');
  model.buyer.firstName = spaceAt > 0 ? name.slice(0, spaceAt) : name;
  model.buyer.lastName = spaceAt > 0 ? name.slice(spaceAt + 1) : '';
  model.buyer.email = deal?.customer_email || '';
  model.buyer.phone = deal?.customer_phone || '';
  model.vehicle = {
    vin: veh.vin || '', stockNumber: veh.stock_number || '',
    year: veh.year || '', make: veh.make || '', model: veh.model || '',
    body: bodyFromVehicle(veh), color: veh.exterior_color || '',
    mileage: veh.mileage || '',
    condition: (veh.condition || 'used').toUpperCase() === 'NEW' ? 'NEW' : 'USED',
  };
  const allowance = num(deal?.trade_in_value);
  const payoff = num(deal?.trade_in_payoff);
  model.trade = {
    hasTrade: allowance > 0 || payoff > 0,
    year: '', make: '', model: '',
    allowance, payoff,
  };
  model.money.salePrice = num(deal?.vehicle_price);
  model.money.cashDown = num(deal?.down_payment);
  if (deal?.sale_date) model.saleDate = String(deal.sale_date).slice(0, 10);
  else if (deal?.created_at) model.saleDate = String(deal.created_at).slice(0, 10);
  if (num(deal?.monthly_payment) > 0) {
    model.dealType = 'financed';
    model.overrides.paymentAmount = num(deal.monthly_payment);
  } else {
    model.dealType = 'cash';
  }
  return model;
}
